import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as fabric from 'fabric';
import {
  apiGet, apiPost, apiPostForm, apiPatch, apiDelete, AuthError, GUARDRAILS_URL,
} from '../../api';
import { auth } from '../../firebase';
import { useClient } from '../../ClientContext';
import Icon from '../../components/Icon';
import {
  SIZES, SIZE, deriveSizeLayout, buildSlotText, pillGeometry, coverImage,
  loadImageElement, ensureFonts, renderCreative, canvasToPngBlob, toPngDataUrl,
  assetName, slugify, regionLuminance,
} from './render';
import './FeedStudio.css';

/**
 * Agentic Studio, feed edition.
 *
 * One base creative, one locked layout, one feed: every version. Six steps,
 * listed down the right so the person always sees where they are:
 *
 *   1 Base creative     upload or pick from the library; amend with AI
 *   2 Layout & text     text slots bound to feed columns, per size
 *   3 Feed              template download, upload, row checks
 *   4 Preview           sample rows in every size, from the real renderer
 *   5 Generate & review render every file, exclude any
 *   6 Links & push      share by email, export links, push to the library
 *
 * WHERE RENDERING HAPPENS
 * In this browser, with render.js. The same function draws the step 4
 * previews and the full-size files, so what was checked is what is produced.
 * The server checks each file is exactly the right pixel size and names it;
 * it never draws.
 *
 * WHAT THIS PAGE CANNOT DO
 * Publish. Push puts files in the library pending approval. Approval uploads
 * to Meta, as it does for any other creative.
 *
 * The original studio at /creative/studio is untouched; this page lives
 * beside it until it replaces it.
 */

const DISPLAY_MAX = 500;

const STEPS = [
  { n: 1, title: 'Base creative',     sub: 'Upload, then amend with AI' },
  { n: 2, title: 'Layout & text',     sub: 'Text slots, fonts, sizes' },
  { n: 3, title: 'Feed',              sub: 'Template, upload, check' },
  { n: 4, title: 'Preview',           sub: 'Sample rows before generating' },
  { n: 5, title: 'Generate & review', sub: 'Exclude any variant' },
  { n: 6, title: 'Links & push',      sub: 'Share, export, Creative Library' },
];

const QUICK_PROMPTS = ['Remove all the text from the image', 'Change the background to ', 'Warmer evening light'];

const FALLBACK_PALETTE = ['#0f2e24', '#e8a33d', '#f7f4ec', '#ffffff', '#16161f'];
const SYSTEM_FONTS = ['Sora', 'Instrument Sans', 'Georgia', 'Helvetica', 'Arial'];

const CAMPAIGN_TYPES = [
  ['OUTCOME_AWARENESS', 'Awareness'], ['OUTCOME_TRAFFIC', 'Traffic'],
  ['OUTCOME_ENGAGEMENT', 'Engagement'], ['OUTCOME_LEADS', 'Leads'],
  ['OUTCOME_SALES', 'Sales'],
];

const RENDER_CONCURRENCY = 3;

function slotPreset(kind, existing, brandBits) {
  const { headingFamily, bodyFamily, ink, accent } = brandBits;
  const presets = {
    headline: { key: 'headline', label: 'Headline', max_chars: 40, maxLines: 2,
      fontFamily: headingFamily, fontWeight: 700, fill: ink, align: 'left', bg: null,
      sample: 'Your headline here', pos: { x: 0.07, y: 0.08, w: 0.72, f: 0.075 } },
    body: { key: 'body', label: 'Body text', max_chars: 60, maxLines: 2,
      fontFamily: bodyFamily, fontWeight: 400, fill: ink, align: 'left', bg: null,
      sample: 'A supporting line', pos: { x: 0.07, y: 0.28, w: 0.72, f: 0.038 } },
    cta: { key: 'cta', label: 'Call to action', max_chars: 18, maxLines: 1,
      fontFamily: bodyFamily, fontWeight: 600, fill: '#ffffff', align: 'left', bg: accent,
      sample: 'Shop now', pos: { x: 0.09, y: 0.86, w: 0.5, f: 0.036 } },
  };
  const base = presets[kind];
  let key = base.key;
  let i = 2;
  const taken = new Set(existing.map((s) => s.key));
  while (taken.has(key)) { key = `${base.key}_${i}`; i += 1; }
  return { ...base, key, label: key === base.key ? base.label : `${base.label} ${i - 1}` };
}

async function downloadAuthed(path, options, filename) {
  const token = await auth.currentUser.getIdToken();
  const response = await fetch(`${GUARDRAILS_URL}${path}`, {
    ...options,
    headers: { ...(options?.headers || {}), Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    let detail = `Download failed (${response.status})`;
    try { detail = (await response.json()).detail || detail; } catch (e) { /* not JSON */ }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function FeedStudio() {
  const { metaAccountId: accountId, selected: client, isAll } = useClient();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [brand, setBrand] = useState({ guidelines: {}, logos: [], fonts: [] });
  const [library, setLibrary] = useState([]);
  const [recent, setRecent] = useState([]);

  // Step 1: the base picture and every version of it.
  const [versions, setVersions] = useState([]);   // [{ dataUrl, label }]
  const [versionIdx, setVersionIdx] = useState(-1);
  const [baseEl, setBaseEl] = useState(null);
  const [prompt, setPrompt] = useState('');

  // Step 2: the layout.
  const [chosenSizes, setChosenSizes] = useState(['1:1', '4:5', '9:16']);
  const [activeSize, setActiveSize] = useState('1:1');
  const [layout, setLayout] = useState({ slots: [], sizes: {}, sample: {}, logoId: null });
  const layoutRef = useRef(layout);
  useEffect(() => { layoutRef.current = layout; }, [layout]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [rebuild, setRebuild] = useState(0);
  const [logoEl, setLogoEl] = useState(null);

  // Step 3: the feed.
  const [campaign, setCampaign] = useState('');
  const [feed, setFeed] = useState(null);         // parse result + fileName
  const feedInputRef = useRef(null);

  // Step 4.
  const [skipProblems, setSkipProblems] = useState(true);
  const [previews, setPreviews] = useState([]);   // [{ row, items: [{ size, url, overflow }] }]

  // Steps 5 and 6.
  const [batch, setBatch] = useState(null);       // { batch, variants, shared_with }
  const [thumbs, setThumbs] = useState({});       // asset_name -> data URL
  const [progress, setProgress] = useState(null); // { done, total, failed: [] }
  const [shareEmail, setShareEmail] = useState('');
  const [campaignType, setCampaignType] = useState('OUTCOME_AWARENESS');

  const editorElRef = useRef(null);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const flash = (m) => { setNotice(m); setError(null); };

  // Notices are confirmations, not state: they clear after a few seconds and
  // whenever the step changes, so "Amended" does not sit over step 4.
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);
  useEffect(() => { setNotice(null); }, [step]);
  const fail = (err, fallback) => {
    setNotice(null);
    setError(err instanceof AuthError ? err.message : (err?.message || fallback));
  };

  // ── Brand, library, recent batches ─────────────────────────────────────────

  const loadRecent = useCallback(() => {
    if (!accountId) return;
    apiGet(`/studio/${accountId}/batches`)
      .then((d) => setRecent(d.batches || []))
      .catch(() => { /* a missing list is not worth a banner */ });
  }, [accountId]);

  useEffect(() => {
    if (!accountId) return;
    apiGet(`/brand/${accountId}?inline=true`)
      .then(setBrand)
      .catch((err) => fail(err, 'Could not load brand guidelines. The studio will open without them.'));
    apiGet(`/creative-library/${accountId}`)
      .then((d) => setLibrary((d.assets || []).filter((a) => a.asset_type === 'IMAGE')))
      .catch(() => {});
    loadRecent();
  }, [accountId, loadRecent]);

  useEffect(() => {
    let cancelled = false;
    (brand.fonts || []).forEach((font) => {
      if (!font.url) return;
      try {
        const face = new FontFace(font.label, `url(${font.url})`, { weight: font.variant || '400' });
        face.load().then((f) => { if (!cancelled) document.fonts.add(f); }).catch(() => {});
      } catch (e) { /* a malformed font falls back */ }
    });
    return () => { cancelled = true; };
  }, [brand.fonts]);

  const palette = (brand.guidelines?.palette?.length
    ? brand.guidelines.palette.map((c) => c.hex)
    : FALLBACK_PALETTE);
  const headingFamily = brand.guidelines?.typography?.heading?.family || 'Sora';
  const bodyFamily = brand.guidelines?.typography?.body?.family || 'Instrument Sans';
  const fontChoices = useMemo(() => {
    const out = [];
    const add = (f) => { if (f && !out.includes(f)) out.push(f); };
    (brand.fonts || []).forEach((f) => add(f.label));
    add(headingFamily); add(bodyFamily);
    SYSTEM_FONTS.forEach(add);
    return out;
  }, [brand.fonts, headingFamily, bodyFamily]);

  const logos = brand.logos || [];
  const chosenLogo = logos.find((l) => l.brand_asset_id === layout.logoId) || null;
  useEffect(() => {
    if (!chosenLogo?.url) { setLogoEl(null); return; }
    loadImageElement(chosenLogo.url).then(setLogoEl).catch(() => setLogoEl(null));
  }, [chosenLogo?.url]);

  // ── Step 1: base ───────────────────────────────────────────────────────────

  const current = versionIdx >= 0 ? versions[versionIdx] : null;
  useEffect(() => {
    if (!current) { setBaseEl(null); return; }
    loadImageElement(current.dataUrl).then(setBaseEl).catch(() => setBaseEl(null));
  }, [current]);

  const addVersion = (dataUrl, label) => {
    setVersions((vs) => {
      const next = [...vs, { dataUrl, label }];
      setVersionIdx(next.length - 1);
      return next;
    });
  };

  const startBase = async (dataUrl, label) => {
    const png = await toPngDataUrl(dataUrl);
    setVersions([{ dataUrl: png, label }]);
    setVersionIdx(0);
  };

  const onUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => startBase(reader.result, 'Original upload').catch((e) => fail(e, 'That image could not be read.'));
    reader.readAsDataURL(file);
  };

  const fromLibrary = async (assetId) => {
    if (!assetId) return;
    setBusy('base'); setError(null);
    try {
      const d = await apiGet(`/creative-library/${accountId}/asset/${assetId}/data`);
      await startBase(`data:${d.content_type};base64,${d.data}`, `From library: ${d.asset_name}`);
    } catch (err) { fail(err, 'That creative could not be loaded.'); }
    setBusy(null);
  };

  /**
   * Generate when there is no picture yet, amend when there is. Only one is
   * offered at a time: the 30 Oct failure was a prompt about "this image"
   * sent through Generate, which never receives the image.
   */
  const runAI = async () => {
    if (!prompt.trim()) return;
    setBusy('ai'); setError(null); setNotice(null);
    try {
      const body = { prompt: prompt.trim(), mode: current ? 'edit' : 'generate', aspect_ratio: chosenSizes[0] || '1:1' };
      if (current) body.source_image = current.dataUrl.split(',')[1];
      const r = await apiPost(`/studio/${accountId}/generate`, body);
      const png = await toPngDataUrl(`data:${r.mime_type};base64,${r.image}`);
      addVersion(png, `“${prompt.trim().slice(0, 60)}”`);
      setPrompt('');
      flash(current ? 'Amended. The previous version is kept below if you want it back.' : 'Generated.');
    } catch (err) { fail(err, 'The image could not be produced.'); }
    setBusy(null);
  };

  // ── Step 2: the layout editor ──────────────────────────────────────────────

  const spec = SIZE[activeSize];
  const scale = Math.min(DISPLAY_MAX / spec.w, DISPLAY_MAX / spec.h);
  const dispW = Math.round(spec.w * scale);
  const dispH = Math.round(spec.h * scale);

  /** Positions on the live canvas back into the layout, as fractions. */
  const readEditor = useCallback(() => {
    const canvas = editorRef.current;
    // Not until it has been drawn: reading a half-built canvas would save an
    // empty layout for this size if someone switched tabs while fonts loaded.
    if (!canvas || !canvas.bsReady) return null;
    const W = canvas.getWidth();
    const H = canvas.getHeight();
    const short = Math.min(W, H);
    const out = { slots: {}, logo: null };
    const sample = { ...layoutRef.current.sample };
    canvas.getObjects().forEach((o) => {
      if (o.bsSlot) {
        out.slots[o.bsSlot] = { x: o.left / W, y: o.top / H, w: (o.width * (o.scaleX || 1)) / W, f: o.bsSetSize / short };
        sample[o.bsSlot] = o.text;
      }
      if (o.bsRole === 'logo') out.logo = { x: o.left / W, y: o.top / H, w: o.getScaledWidth() / W };
    });
    return { sizeLayout: out, sample };
  }, []);

  const commitEditor = useCallback((sizeKey) => {
    const read = readEditor();
    if (!read) return layoutRef.current;
    const next = {
      ...layoutRef.current,
      sample: read.sample,
      sizes: { ...layoutRef.current.sizes, [sizeKey]: read.sizeLayout },
    };
    layoutRef.current = next;
    setLayout(next);
    return next;
  }, [readEditor]);

  useEffect(() => {
    if (step !== 2 || !editorElRef.current) return undefined;
    const canvas = new fabric.Canvas(editorElRef.current, {
      width: dispW, height: dispH, backgroundColor: '#ffffff', preserveObjectStacking: true,
    });
    editorRef.current = canvas;
    const sizeKey = activeSize;
    const lay = layoutRef.current;
    const sizeLayout = deriveSizeLayout(lay, sizeKey);
    const pills = {};

    let cancelled = false;
    (async () => {
      await ensureFonts(lay);
      if (cancelled) return;
      if (baseEl) canvas.add(coverImage(fabric, baseEl, dispW, dispH));
      lay.slots.forEach((slotDef) => {
        const pos = sizeLayout.slots?.[slotDef.key] || slotPreset('body', [], {}).pos;
        const { tb } = buildSlotText(fabric, slotDef, pos, lay.sample[slotDef.key] ?? '', dispW, dispH);
        tb.bsSetSize = pos.f * Math.min(dispW, dispH);
        tb.set({ editable: true, lockScalingY: true, lockRotation: true });
        tb.setControlsVisibility({ tl: false, tr: false, bl: false, br: false, mt: false, mb: false, mtr: false });
        if (slotDef.bg) {
          const pill = new fabric.Rect({ ...pillGeometry(tb, slotDef.align), fill: slotDef.bg, selectable: false, evented: false });
          pills[slotDef.key] = pill;
          canvas.add(pill);
        }
        canvas.add(tb);
        if (slotDef.key === selectedKey) canvas.setActiveObject(tb);
      });
      if (logoEl && sizeLayout.logo) {
        const img = new fabric.FabricImage(logoEl);
        img.scaleToWidth(sizeLayout.logo.w * dispW);
        img.set({ left: sizeLayout.logo.x * dispW, top: sizeLayout.logo.y * dispH, lockRotation: true });
        img.bsRole = 'logo';
        canvas.add(img);
      }
      canvas.bsReady = true;
      canvas.requestRenderAll();
    })();

    const syncPill = (o) => {
      if (!o?.bsSlot || !pills[o.bsSlot]) return;
      const def = layoutRef.current.slots.find((s) => s.key === o.bsSlot);
      pills[o.bsSlot].set(pillGeometry(o, def?.align));
      pills[o.bsSlot].setCoords();
    };
    const onSelect = () => setSelectedKey(canvas.getActiveObject()?.bsSlot || null);
    canvas.on('selection:created', onSelect);
    canvas.on('selection:updated', onSelect);
    canvas.on('selection:cleared', () => setSelectedKey(null));
    canvas.on('object:moving', (e) => syncPill(e.target));
    canvas.on('object:resizing', (e) => syncPill(e.target));
    canvas.on('text:changed', (e) => {
      syncPill(e.target);
      const sample = { ...layoutRef.current.sample, [e.target.bsSlot]: e.target.text };
      layoutRef.current = { ...layoutRef.current, sample };
      setLayout(layoutRef.current);
    });
    canvas.on('object:modified', (e) => { syncPill(e.target); commitEditor(sizeKey); });

    return () => {
      cancelled = true;
      commitEditor(sizeKey);
      canvas.dispose();
      editorRef.current = null;
    };
    // selectedKey is read once, to keep the selection across a rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, activeSize, dispW, dispH, baseEl, logoEl, rebuild, commitEditor]);

  /** Change the layout structurally, then redraw the editor from it. */
  const changeLayout = (fn) => {
    const committed = editorRef.current ? commitEditor(activeSize) : layoutRef.current;
    const next = fn(committed);
    layoutRef.current = next;
    setLayout(next);
    setRebuild((r) => r + 1);
  };

  const addSlot = (kind) => changeLayout((lay) => {
    const def = slotPreset(kind, lay.slots, { headingFamily, bodyFamily, ink: palette[0], accent: palette[0] });
    // Start with a colour that reads against the picture behind the slot.
    const dark = regionLuminance(baseEl, activeSize, def.pos, def.maxLines) < 0.5;
    if (def.bg) {
      def.bg = dark ? '#ffffff' : palette[0];
      def.fill = dark ? palette[0] : '#ffffff';
    } else {
      def.fill = dark ? '#ffffff' : palette[0];
    }
    const { sample, pos, ...slotDef } = def;
    const sizes = { ...lay.sizes };
    SIZES.forEach((s) => {
      const sl = deriveSizeLayout({ ...lay, sizes }, s.key);
      sizes[s.key] = { ...sl, slots: { ...sl.slots, [slotDef.key]: pos } };
    });
    setSelectedKey(slotDef.key);
    return { ...lay, slots: [...lay.slots, slotDef], sizes, sample: { ...lay.sample, [slotDef.key]: sample } };
  });

  const updateSlot = (key, patch) => changeLayout((lay) => {
    const slots = lay.slots.map((s) => (s.key === key ? { ...s, ...patch } : s));
    let sizes = lay.sizes;
    let sample = lay.sample;
    if (patch.key !== undefined && patch.key !== key) {
      // Renaming the feed column moves its positions and sample text with it.
      sizes = Object.fromEntries(Object.entries(lay.sizes).map(([k, v]) => {
        const { [key]: moved, ...rest } = v.slots || {};
        return [k, { ...v, slots: moved ? { ...rest, [patch.key]: moved } : rest }];
      }));
      const { [key]: text, ...restSample } = lay.sample;
      sample = { ...restSample, [patch.key]: text };
      setSelectedKey(patch.key);
    }
    return { ...lay, slots, sizes, sample };
  });

  /** Font size is per format, so it is written to this size's position only. */
  const setFontPx = (key, px) => changeLayout((lay) => {
    const sl = deriveSizeLayout(lay, activeSize);
    const pos = sl.slots?.[key];
    if (!pos || !px) return lay;
    const f = Number(px) / Math.min(spec.w, spec.h);
    return { ...lay, sizes: { ...lay.sizes, [activeSize]: { ...sl, slots: { ...sl.slots, [key]: { ...pos, f } } } } };
  });

  const removeSlot = (key) => changeLayout((lay) => {
    setSelectedKey(null);
    return {
      ...lay,
      slots: lay.slots.filter((s) => s.key !== key),
      sizes: Object.fromEntries(Object.entries(lay.sizes).map(([k, v]) => {
        const { [key]: _gone, ...rest } = v.slots || {};
        return [k, { ...v, slots: rest }];
      })),
    };
  });

  const setLogo = (brandAssetId) => changeLayout((lay) => {
    const sizes = { ...lay.sizes };
    SIZES.forEach((s) => {
      const sl = deriveSizeLayout({ ...lay, sizes }, s.key);
      sizes[s.key] = { ...sl, logo: brandAssetId ? (sl.logo || { x: 0.78, y: 0.06, w: 0.16 }) : null };
    });
    return { ...lay, logoId: brandAssetId || null, sizes };
  });

  const switchSize = (key) => {
    if (editorRef.current) commitEditor(activeSize);
    setActiveSize(key);
  };

  const toggleSize = (key) => {
    const next = chosenSizes.includes(key)
      ? chosenSizes.filter((k) => k !== key)
      : SIZES.map((s) => s.key).filter((k) => chosenSizes.includes(k) || k === key);
    if (!next.length) return;   // at least one size, always
    if (!next.includes(activeSize)) switchSize(next[0]);
    setChosenSizes(next);
  };

  const selectedDef = layout.slots.find((s) => s.key === selectedKey) || null;
  const selectedPos = selectedDef ? deriveSizeLayout(layout, activeSize).slots?.[selectedDef.key] : null;
  const selectedPx = selectedPos ? Math.round(selectedPos.f * Math.min(spec.w, spec.h)) : '';
  const sampleLen = selectedDef ? (layout.sample[selectedDef.key] || '').length : 0;

  // ── Step 3: feed ───────────────────────────────────────────────────────────

  const slotPayload = layout.slots.map((s) => ({ key: s.key, label: s.label, max_chars: Number(s.max_chars) }));

  const downloadTemplate = async () => {
    setError(null);
    try {
      await downloadAuthed(`/studio/${accountId}/feed/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slots: slotPayload, sizes: chosenSizes,
          example: { variant: 'example', ...layout.sample },
        }),
      }, `bidsense-creative-template-${slugify(campaign) || 'feed'}.xlsx`);
    } catch (err) { fail(err, 'Could not make the template.'); }
  };

  const uploadFeed = async (file) => {
    if (!file) return;
    setBusy('feed'); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slots', JSON.stringify(slotPayload));
      fd.append('sizes', JSON.stringify(chosenSizes));
      const r = await apiPostForm(`/studio/${accountId}/feed/parse`, fd);
      setFeed({ ...r, fileName: file.name });
      setPreviews([]);
    } catch (err) { fail(err, 'The feed could not be read.'); setFeed(null); }
    setBusy(null);
    if (feedInputRef.current) feedInputRef.current.value = '';
  };

  const blocking = (row) => row.problems.some((p) => p.startsWith('variant') || p.startsWith('same variant'));
  const rowsToMake = feed ? feed.rows.filter((r) => (skipProblems ? !r.problems.length : !blocking(r))) : [];
  const jobs = rowsToMake.flatMap((r) => r.sizes.filter((s) => chosenSizes.includes(s)).map((s) => ({ row: r, size: s })));
  const brandName = client?.name || '';

  // ── Step 4: previews ───────────────────────────────────────────────────────

  useEffect(() => {
    if (step !== 4 || !feed) return undefined;
    let cancelled = false;
    (async () => {
      await ensureFonts(layout);
      const sampleRows = [...feed.rows.filter((r) => r.problems.length).slice(0, 1),
        ...feed.rows.filter((r) => !r.problems.length).slice(0, 2)].sort((a, b) => a.row - b.row);
      const out = sampleRows.map((row) => ({
        row,
        items: chosenSizes.filter((s) => row.sizes.includes(s)).map((size) => {
          const outW = size === '1.91:1' ? 260 : size === '9:16' ? 140 : 180;
          const { canvas, overflow } = renderCreative({ fabric, base: baseEl, logoEl, layout, sizeKey: size, values: row.values, outW });
          const url = canvas.toDataURL({ format: 'png' });
          canvas.dispose();
          return { size, url, overflow };
        }),
      }));
      if (!cancelled) setPreviews(out);
    })();
    return () => { cancelled = true; };
  }, [step, feed, layout, chosenSizes, baseEl, logoEl]);

  // ── Step 5: generate ───────────────────────────────────────────────────────

  const loadBatch = useCallback(async (batchId) => {
    const d = await apiGet(`/studio/${accountId}/batches/${batchId}`);
    setBatch(d);
    return d;
  }, [accountId]);

  const generate = async () => {
    if (!jobs.length) return;
    setBusy('generate'); setError(null); setNotice(null);
    setThumbs({});
    setStep(5);
    try {
      const created = await apiPost(`/studio/${accountId}/batches`, {
        name: campaign, sizes: chosenSizes, slots: slotPayload, layout,
      });
      const batchId = created.batch_id;
      await ensureFonts(layout);
      const state = { done: 0, total: jobs.length, failed: [] };
      setProgress({ ...state });
      const queue = [...jobs];
      const worker = async () => {
        while (queue.length) {
          const job = queue.shift();
          const { canvas } = renderCreative({ fabric, base: baseEl, logoEl, layout, sizeKey: job.size, values: job.row.values });
          try {
            const blob = await canvasToPngBlob(canvas);
            // A small copy for the gallery, drawn from the real file.
            const t = document.createElement('canvas');
            const tw = 150;
            t.width = tw;
            t.height = Math.round(tw * (canvas.getHeight() / canvas.getWidth()));
            t.getContext('2d').drawImage(canvas.getElement(), 0, 0, t.width, t.height);
            const name = assetName(brandName, campaign, job.row.variant, job.size);
            setThumbs((m) => ({ ...m, [name]: t.toDataURL('image/png') }));

            const fd = new FormData();
            fd.append('file', blob, `${name}.png`);
            fd.append('size', job.size);
            fd.append('variant', job.row.variant);
            fd.append('row_number', String(job.row.row));
            fd.append('row_values', JSON.stringify(job.row.values));
            await apiPostForm(`/studio/${accountId}/batches/${batchId}/variants`, fd);
          } catch (err) {
            state.failed.push(`${job.row.variant} ${SIZE[job.size].tag}: ${err.message}`);
          } finally {
            canvas.dispose();
            state.done += 1;
            setProgress({ ...state });
          }
        }
      };
      await Promise.all(Array.from({ length: RENDER_CONCURRENCY }, worker));
      await loadBatch(batchId);
      loadRecent();
      if (state.failed.length) setError(`${state.failed.length} of ${state.total} could not be stored. ${state.failed.slice(0, 3).join('; ')}`);
      else flash(`${state.total} creatives generated.`);
    } catch (err) { fail(err, 'The batch could not be started.'); }
    setBusy(null);
  };

  const toggleExclude = async (v) => {
    try {
      await apiPatch(`/studio/${accountId}/batches/${batch.batch.batch_id}/variants/${v.variant_id}`, { excluded: !v.excluded });
      setBatch((b) => ({ ...b, variants: b.variants.map((x) => (x.variant_id === v.variant_id ? { ...x, excluded: !v.excluded } : x)) }));
    } catch (err) { fail(err, 'Could not change that.'); }
  };

  // ── Step 6: share, export, push ────────────────────────────────────────────

  const batchId = batch?.batch?.batch_id;

  const addShare = async () => {
    if (!shareEmail.trim()) return;
    setBusy('share'); setError(null);
    try {
      const r = await apiPost(`/studio/${accountId}/batches/${batchId}/shares`, { email: shareEmail.trim() });
      setBatch((b) => ({ ...b, shared_with: r.shared_with }));
      setShareEmail('');
      flash('Added. Google can take a few minutes to apply a new address.');
    } catch (err) { fail(err, 'Could not share with that address.'); }
    setBusy(null);
  };

  const removeShare = async (email) => {
    setBusy('share'); setError(null);
    try {
      const r = await apiDelete(`/studio/${accountId}/batches/${batchId}/shares?email=${encodeURIComponent(email)}`);
      setBatch((b) => ({ ...b, shared_with: r.shared_with }));
      flash(`${email} removed. Anything they already downloaded stays with them.`);
    } catch (err) { fail(err, 'Could not remove that address.'); }
    setBusy(null);
  };

  const exportLinks = async () => {
    setError(null);
    try {
      await downloadAuthed(`/studio/${accountId}/batches/${batchId}/export`, {},
        `${slugify(batch.batch.name) || 'batch'}-creative-links.xlsx`);
    } catch (err) { fail(err, 'Could not export the links.'); }
  };

  const push = async () => {
    setBusy('push'); setError(null);
    try {
      const r = await apiPost(`/studio/${accountId}/batches/${batchId}/push`, { campaign_type: campaignType });
      await loadBatch(batchId);
      flash(r.message);
    } catch (err) { fail(err, 'Could not push to the library.'); }
    setBusy(null);
  };

  const approveBatch = async () => {
    setBusy('approve'); setError(null);
    try {
      const r = await apiPost(`/creative-library/${accountId}/batches/${batchId}/approve`, {});
      await loadBatch(batchId);
      if (r.failed.length) setError(`${r.approved} approved; ${r.failed.length} could not be: ${r.failed[0].error}`);
      else flash(`${r.approved} approved and uploaded to Meta. Agents can now use them.`);
    } catch (err) { fail(err, 'Could not approve the batch.'); }
    setBusy(null);
  };

  const openRecent = async (id) => {
    if (!id) return;
    setError(null);
    try {
      await loadBatch(id);
      setThumbs({});
      setStep(6);
    } catch (err) { fail(err, 'Could not open that batch.'); }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────

  // What stops a step being finished. Used by Next AND by the steps rail, so a
  // later step can never be reached by clicking past an unfinished one (30 Oct:
  // the rail skipped step 2 and the template was requested with no slots).
  const blockerFor = (s) => {
    if (s === 1 && !current) return 'Upload or generate a base image first.';
    if (s === 2 && !layout.slots.length) return 'Add at least one text slot in step 2.';
    if (s === 3 && !slugify(campaign)) return 'Give the campaign a name; it goes into every asset name.';
    if (s === 3 && !feed) return 'Upload a feed.';
    if (s === 4 && !jobs.length) return 'No rows to generate.';
    if (s === 5 && (busy === 'generate' || !batch)) return 'Wait for generation to finish.';
    return null;
  };
  const nextBlocker = blockerFor(step);
  const nextLabel = ['Continue to layout', 'Lock layout & continue', 'Continue to preview',
    `Generate ${jobs.length} creative${jobs.length === 1 ? '' : 's'}`, 'Continue to links', ''][step - 1];
  const goNext = () => {
    if (nextBlocker) { setError(nextBlocker); return; }
    setError(null);
    if (step === 4) { generate(); return; }
    setStep(step + 1);
  };
  const goTo = (n) => {
    if (busy === 'generate') return;
    if (n >= 5) {
      if (!batch) { setError('Generate the creatives first.'); return; }
    } else {
      // Steps 2-4 need every earlier step finished in this session. A batch
      // reopened from the list has its files but not its layout or feed, so it
      // opens at steps 5 and 6 only.
      for (let s = 1; s < n; s += 1) {
        const b = blockerFor(s);
        if (b) { setError(b); return; }
      }
    }
    setError(null);
    setStep(n);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isAll || !accountId) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Agentic Studio</h1>
          <p className="page-subtitle">Creative is built for one client at a time</p>
        </div>
        <div className="card"><div className="fs-empty">
          <h3>Select a client</h3>
          <p>Choose a client from the switcher above to open the studio with their brand.</p>
        </div></div>
      </>
    );
  }

  const variants = batch?.variants || [];
  const included = variants.filter((v) => !v.excluded);
  const notPushed = included.filter((v) => !v.library_asset_id);
  const pending = variants.filter((v) => v.status === 'Pending approval');
  const galleryRows = Object.values(variants.reduce((acc, v) => {
    (acc[v.row_number] = acc[v.row_number] || { row: v.row_number, values: v.row_values, items: [] }).items.push(v);
    return acc;
  }, {})).sort((a, b) => a.row - b.row);

  return (
    <div className="fs">
      <div className="page-header split">
        <div>
          <h1 className="page-title">Agentic Studio</h1>
          <p className="page-subtitle">
            {client ? `${client.name} · ` : ''}one base creative, one feed, every version. Nothing reaches Meta without approval.
          </p>
        </div>
        <div className="hdr-actions">
          {recent.length > 0 && (
            <select className="form-input fs-recent" value="" onChange={(e) => openRecent(e.target.value)} aria-label="Open a recent batch">
              <option value="">Open a recent batch…</option>
              {recent.map((b) => (
                <option key={b.batch_id} value={b.batch_id}>
                  {b.name} · {b.variant_count} files · {b.status}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && <div className="banner bad"><Icon name="alert" size={16} strokeWidth={2.2} /><span>{error}</span></div>}
      {notice && <div className="banner ok"><Icon name="check" size={16} strokeWidth={2.2} /><span>{notice}</span></div>}

      <div className="fs-grid">
        <section className="card fs-work" aria-label={`Step ${step}: ${STEPS[step - 1].title}`}>

          {step === 1 && (
            <div className="fs-col">
              <div className="fs-row-between">
                <div className="fs-eyebrow">Base creative{current ? ` · version ${versionIdx + 1} of ${versions.length}` : ''}</div>
                {baseEl && <div className="fs-mono fs-muted">{baseEl.naturalWidth}×{baseEl.naturalHeight}</div>}
              </div>
              <div className="fs-stage">
                {current ? <img src={current.dataUrl} alt="Base creative" className="fs-base" />
                  : (
                    <div className="fs-empty">
                      <Icon name="image" size={28} />
                      <h3>No base yet</h3>
                      <p>Upload an image or pick one from the library. A finished ad with text already on it works too: prompt to remove the text, then lay new text over it.</p>
                    </div>
                  )}
              </div>
              {versions.length > 1 && (
                <div className="fs-col fs-gap-s">
                  <div className="fs-eyebrow">Version history</div>
                  <div className="fs-versions">
                    {versions.map((v, i) => (
                      <button key={i} type="button" className={`fs-version${i === versionIdx ? ' on' : ''}`} onClick={() => setVersionIdx(i)}>
                        <img src={v.dataUrl} alt="" />
                        <span><b>v{i + 1}</b><span>{v.label}</span></span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="fs-col">
              <div className="fs-tabs" role="tablist" aria-label="Size being laid out">
                {SIZES.filter((s) => chosenSizes.includes(s.key)).map((s) => (
                  <button key={s.key} type="button" role="tab" aria-selected={s.key === activeSize}
                    className={`fs-tab${s.key === activeSize ? ' on' : ''}`} onClick={() => switchSize(s.key)}>
                    {s.label} <span className="fs-mono">{s.key}</span>
                  </button>
                ))}
                <span className="fs-muted fs-small fs-push-right">Each size keeps its own positions. Check every tab once.</span>
              </div>
              <div className="fs-editor-row">
                <div className="fs-stage">
                  <canvas ref={editorElRef} />
                </div>
                <div className="fs-col fs-gap-s fs-layers">
                  <div className="fs-eyebrow">Text slots</div>
                  {layout.slots.length === 0 && <p className="fs-muted fs-small">Add a headline, body or call to action. Each becomes a feed column.</p>}
                  {layout.slots.map((s) => (
                    <button key={s.key} type="button" className={`fs-layer${s.key === selectedKey ? ' on' : ''}`} onClick={() => {
                      const o = editorRef.current?.getObjects().find((x) => x.bsSlot === s.key);
                      if (o) { editorRef.current.setActiveObject(o); editorRef.current.requestRenderAll(); }
                      setSelectedKey(s.key);
                    }}>
                      <span><b>{s.label}</b><span className="fs-mono">{s.key}</span></span>
                      <span className="fs-muted fs-small">≤{s.max_chars}</span>
                    </button>
                  ))}
                  <div className="fs-btn-row">
                    <button type="button" className="btn btn-secondary" onClick={() => addSlot('headline')}><Icon name="plus" size={13} />Headline</button>
                    <button type="button" className="btn btn-secondary" onClick={() => addSlot('body')}><Icon name="plus" size={13} />Body</button>
                    <button type="button" className="btn btn-secondary" onClick={() => addSlot('cta')}><Icon name="plus" size={13} />CTA</button>
                  </div>
                  <p className="fs-muted fs-small">The text on the canvas is sample text. Type over it to try lengths; the feed replaces it.</p>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="fs-col">
              <div className="fs-btn-row">
                <button type="button" className="btn btn-secondary" onClick={downloadTemplate} disabled={!layout.slots.length}
                  title={layout.slots.length ? undefined : 'Add text slots in step 2 first'}>
                  <Icon name="download" size={14} />Download creative template
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => feedInputRef.current?.click()} disabled={busy === 'feed'}>
                  {busy === 'feed' ? 'Reading…' : feed ? 'Replace feed' : 'Upload feed (.xlsx or .csv)'}
                </button>
                <input ref={feedInputRef} type="file" accept=".xlsx,.csv" hidden onChange={(e) => uploadFeed(e.target.files?.[0])} />
                {feed && <span className="fs-mono fs-small">{feed.fileName} · {feed.row_count} rows · {feed.problem_rows} with problems</span>}
              </div>
              {!feed && (
                <div className="fs-empty">
                  <Icon name="cards" size={26} />
                  <h3>No feed yet</h3>
                  {layout.slots.length ? (
                    <p>The template has one column per text slot ({layout.slots.map((s) => s.key).join(', ')}), a variant column used in asset names, and an optional sizes column. Excel stops anyone typing past a character limit.</p>
                  ) : (
                    <p>There are no text slots yet, so there is nothing for a feed to fill. Go back to step 2 and add a headline, body or call to action.</p>
                  )}
                </div>
              )}
              {feed && (
                <div className="fs-table-wrap">
                  <table className="fs-table">
                    <thead><tr>
                      <th>Row</th><th className="fs-mono">variant</th>
                      {layout.slots.map((s) => <th key={s.key} className="fs-mono">{s.key} <span className="fs-muted">≤{s.max_chars}</span></th>)}
                      <th>Sizes</th><th>Check</th>
                    </tr></thead>
                    <tbody>
                      {feed.rows.map((r) => (
                        <tr key={r.row} className={r.problems.length ? 'bad' : ''}>
                          <td className="fs-mono">{r.row}</td>
                          <td>{r.variant}</td>
                          {layout.slots.map((s) => <td key={s.key}>{r.values[s.key]}</td>)}
                          <td className="fs-mono fs-small">{r.sizes.map((k) => SIZE[k].tag).join(' ')}</td>
                          <td className={r.problems.length ? 'fs-bad' : 'fs-ok'}>{r.problems.length ? r.problems.join('; ') : 'OK'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="fs-col">
              <div className="fs-row-between">
                <div className="fs-eyebrow">Sample rows in every chosen size</div>
                <span className="fs-muted fs-small">Drawn by the same renderer that makes the final files</span>
              </div>
              {!previews.length && <p className="fs-muted">Drawing previews…</p>}
              {previews.map(({ row, items }) => (
                <div key={row.row} className={`fs-sample${row.problems.length ? ' bad' : ''}`}>
                  <div className="fs-btn-row">
                    <span className="fs-mono fs-small">Row {row.row}</span>
                    <b>{row.variant}</b>
                    {row.problems.length > 0 && (
                      <span className="fs-bad fs-small">
                        {row.problems.join('; ')} · {skipProblems ? 'will be skipped' : (blocking(row) ? 'cannot be generated' : 'will generate as shown')}
                      </span>
                    )}
                  </div>
                  <div className="fs-thumbs">
                    {items.map((it) => (
                      <figure key={it.size} className={it.overflow.length ? 'over' : ''}>
                        <img src={it.url} alt={`${row.variant}, ${SIZE[it.size].label}`} />
                        <figcaption className="fs-mono">
                          {SIZE[it.size].tag}{it.overflow.length ? ` · ${it.overflow.join(', ')} doesn't fit` : ''}
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 5 && (
            <div className="fs-col">
              {progress && (
                <div className="fs-col fs-gap-s">
                  <div className="fs-row-between">
                    <b>{progress.done} of {progress.total} rendered</b>
                    <span className="fs-muted fs-small">
                      {variants.length ? `${included.length} included · ${variants.length - included.length} excluded` : ''}
                    </span>
                  </div>
                  <div className="fs-bar"><div style={{ width: `${(progress.done / Math.max(1, progress.total)) * 100}%` }} /></div>
                </div>
              )}
              <div className="fs-gallery">
                {galleryRows.map((g) => (
                  <div key={g.row} className="fs-gallery-card">
                    <div className="fs-btn-row"><span className="fs-mono fs-small">Row {g.row}</span></div>
                    <div className="fs-thumbs">
                      {g.items.map((v) => (
                        <figure key={v.variant_id} className={v.excluded ? 'out' : ''}>
                          {thumbs[v.asset_name]
                            ? <img src={thumbs[v.asset_name]} alt={v.asset_name} />
                            : <div className="fs-thumb-ph fs-mono">{SIZE[v.size]?.tag}</div>}
                          <button type="button" className={`fs-chip${v.excluded ? '' : ' on'}`} onClick={() => toggleExclude(v)}
                            aria-label={`${v.excluded ? 'Include' : 'Exclude'} ${v.asset_name}`} disabled={!!v.library_asset_id}>
                            {v.library_asset_id ? 'In library' : v.excluded ? 'Excluded' : 'Included'}
                          </button>
                        </figure>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 6 && batch && (
            <div className="fs-col">
              <div className="fs-row-between">
                <div className="fs-eyebrow">{batch.batch.name} · {included.length} creatives</div>
                <span className="fs-muted fs-small">Links open only for the addresses this batch is shared with</span>
              </div>
              <div className="fs-table-wrap">
                <table className="fs-table">
                  <thead><tr><th>Asset name</th><th>Size</th><th>Link</th><th>Status</th></tr></thead>
                  <tbody>
                    {variants.map((v) => (
                      <tr key={v.variant_id} className={v.excluded ? 'out' : ''}>
                        <td className="fs-mono fs-small">{v.asset_name}</td>
                        <td className="fs-small">{v.width}×{v.height}</td>
                        <td><a href={v.link} target="_blank" rel="noreferrer" className="fs-mono fs-small">Open</a></td>
                        <td className="fs-small">{v.excluded ? 'Excluded' : v.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <aside className="card fs-panel">
          <nav aria-label="Studio steps" className="fs-steps">
            {STEPS.map((s) => {
              const state = s.n < step ? 'done' : s.n === step ? 'cur' : 'todo';
              return (
                <button key={s.n} type="button" className={`fs-step ${state}`} onClick={() => goTo(s.n)} aria-current={state === 'cur' ? 'step' : undefined}>
                  <span className="fs-dot">{state === 'done' ? <Icon name="check" size={12} strokeWidth={3} /> : s.n}</span>
                  <span><b>{s.title}</b><span>{s.sub}</span></span>
                </button>
              );
            })}
          </nav>
          <hr />

          {step === 1 && (
            <div className="fs-col fs-gap-s">
              <div className="fs-eyebrow">Start from</div>
              <div className="fs-btn-row">
                <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()}>Upload image</button>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => onUpload(e.target.files?.[0])} />
              </div>
              <select className="form-input" value="" onChange={(e) => fromLibrary(e.target.value)} disabled={busy === 'base'} aria-label="Start from a library creative">
                <option value="">{busy === 'base' ? 'Loading…' : 'From creative library…'}</option>
                {library.map((a) => <option key={a.asset_id} value={a.asset_id}>{a.asset_name}</option>)}
              </select>
              <div className="fs-eyebrow fs-mt">{current ? 'Amend with AI' : 'Or generate with AI'}</div>
              <label htmlFor="fs-prompt" className="fs-muted fs-small">
                {current
                  ? 'Anything can change: remove text, swap the background, relight. Each amend is a new version, so you can step back.'
                  : 'Describe the picture. Text is best added in the next step rather than generated.'}
              </label>
              <textarea id="fs-prompt" className="form-input" rows={3} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                placeholder={current ? 'e.g. Remove all the text from the image' : 'e.g. A bright living room at dusk, empty wall on the left'} />
              {current && (
                <div className="fs-chips">
                  {QUICK_PROMPTS.map((q) => <button key={q} type="button" className="fs-chip" onClick={() => setPrompt(q)}>{q.trim()}</button>)}
                </div>
              )}
              <button type="button" className="btn btn-brass" onClick={runAI} disabled={busy === 'ai' || !prompt.trim()}>
                {busy === 'ai' ? 'Working…' : current ? 'Amend image' : 'Generate image'}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="fs-col fs-gap-s">
              <div className="fs-row-between">
                <div className="fs-eyebrow">Sizes to produce</div>
                <button type="button" className="fs-link" onClick={() => setChosenSizes(SIZES.map((s) => s.key))}>All sizes</button>
              </div>
              <div className="fs-size-grid">
                {SIZES.map((s) => {
                  const on = chosenSizes.includes(s.key);
                  return (
                    <button key={s.key} type="button" aria-pressed={on} className={`fs-size${on ? ' on' : ''}`} onClick={() => toggleSize(s.key)}>
                      <b>{on ? '✓' : '+'} {s.label}</b><span className="fs-mono">{s.w}×{s.h}</span>
                    </button>
                  );
                })}
              </div>
              {logos.length > 0 && (
                <label className="fs-field">Logo
                  <select className="form-input" value={layout.logoId || ''} onChange={(e) => setLogo(e.target.value)}>
                    <option value="">No logo</option>
                    {logos.map((l) => <option key={l.brand_asset_id} value={l.brand_asset_id}>{l.label}</option>)}
                  </select>
                </label>
              )}
              {selectedDef ? (
                <div className="fs-col fs-gap-s fs-mt">
                  <div className="fs-row-between">
                    <div className="fs-eyebrow">Selected · {selectedDef.label}</div>
                    <button type="button" className="fs-link fs-bad" onClick={() => removeSlot(selectedDef.key)}>Remove</button>
                  </div>
                  <div className="fs-field-grid">
                    <label className="fs-field">Feed column
                      <input className="form-input fs-mono" defaultValue={selectedDef.key} key={`k-${selectedDef.key}`}
                        onBlur={(e) => {
                          const v = e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
                          if (v && v !== selectedDef.key && !layout.slots.some((s) => s.key === v)) updateSlot(selectedDef.key, { key: v });
                        }} />
                    </label>
                    <label className="fs-field">Character limit
                      <input className="form-input" type="number" min={1} max={500} defaultValue={selectedDef.max_chars} key={`m-${selectedDef.key}`}
                        onBlur={(e) => updateSlot(selectedDef.key, { max_chars: Math.max(1, Number(e.target.value) || 1) })} />
                    </label>
                    <label className="fs-field">Font
                      <select className="form-input" value={selectedDef.fontFamily} onChange={(e) => updateSlot(selectedDef.key, { fontFamily: e.target.value })}>
                        {fontChoices.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </label>
                    <label className="fs-field">Weight
                      <select className="form-input" value={selectedDef.fontWeight} onChange={(e) => updateSlot(selectedDef.key, { fontWeight: Number(e.target.value) })}>
                        {[300, 400, 500, 600, 700, 800].map((w) => <option key={w} value={w}>{w}</option>)}
                      </select>
                    </label>
                    <label className="fs-field">Size ({SIZE[activeSize].label}, px)
                      <input className="form-input" type="number" min={8} max={400} defaultValue={selectedPx} key={`s-${selectedDef.key}-${activeSize}-${selectedPx}`}
                        onBlur={(e) => setFontPx(selectedDef.key, e.target.value)} />
                    </label>
                    <label className="fs-field">Lines allowed
                      <select className="form-input" value={selectedDef.maxLines} onChange={(e) => updateSlot(selectedDef.key, { maxLines: Number(e.target.value) })}>
                        {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                    <label className="fs-field">Align
                      <select className="form-input" value={selectedDef.align} onChange={(e) => updateSlot(selectedDef.key, { align: e.target.value })}>
                        <option value="left">Left</option><option value="center">Centre</option><option value="right">Right</option>
                      </select>
                    </label>
                  </div>
                  <div className="fs-field">Text colour
                    <div className="fs-swatches">
                      {palette.map((hex) => <button key={hex} type="button" className={`fs-swatch${selectedDef.fill === hex ? ' on' : ''}`} style={{ background: hex }} aria-label={`Text colour ${hex}`} onClick={() => updateSlot(selectedDef.key, { fill: hex })} />)}
                    </div>
                  </div>
                  <div className="fs-field">Background
                    <div className="fs-swatches">
                      <button type="button" className={`fs-swatch none${!selectedDef.bg ? ' on' : ''}`} aria-label="No background" onClick={() => updateSlot(selectedDef.key, { bg: null })} />
                      {palette.map((hex) => <button key={hex} type="button" className={`fs-swatch${selectedDef.bg === hex ? ' on' : ''}`} style={{ background: hex }} aria-label={`Background ${hex}`} onClick={() => updateSlot(selectedDef.key, { bg: hex })} />)}
                    </div>
                  </div>
                  <p className={`fs-small ${sampleLen > selectedDef.max_chars ? 'fs-bad' : 'fs-muted'}`}>
                    Sample text: {sampleLen}/{selectedDef.max_chars} characters. Text that wraps past {selectedDef.maxLines} line{selectedDef.maxLines > 1 ? 's' : ''} shrinks to fit, then is flagged.
                  </p>
                </div>
              ) : (
                <p className="fs-muted fs-small fs-mt">Click a text slot on the canvas to set its feed column, limit, font and colour.</p>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="fs-col fs-gap-s">
              <label className="fs-field">Campaign name
                <input className="form-input" value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="e.g. Autumn Sale" maxLength={60} />
              </label>
              <div className="fs-eyebrow fs-mt">Naming convention</div>
              <div className="fs-code">{'{client}_{campaign}_{variant}_{size}'}</div>
              <p className="fs-muted fs-small">
                e.g. <span className="fs-mono">{assetName(brandName, campaign || 'campaign', 'leeds', chosenSizes[0] || '1:1')}</span>
              </p>
              <div className="fs-eyebrow fs-mt">Feed columns</div>
              {[{ key: 'variant', label: 'asset name only' }, ...layout.slots.map((s) => ({ key: s.key, label: `${s.label}, ≤${s.max_chars}` })), { key: 'sizes', label: 'optional, narrows a row' }].map((m) => (
                <div key={m.key} className="fs-map"><span className="fs-mono">{m.key}</span><span className="fs-muted fs-small">→ {m.label}</span></div>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="fs-col fs-gap-s">
              <div className="fs-eyebrow">What will be generated</div>
              <div className="fs-big">{jobs.length}</div>
              <p className="fs-muted fs-small">{rowsToMake.length} rows across {chosenSizes.length} size{chosenSizes.length > 1 ? 's' : ''}{feed?.problem_rows ? `. ${feed.problem_rows} row${feed.problem_rows > 1 ? 's have' : ' has'} problems.` : '.'}</p>
              {feed?.problem_rows > 0 && (
                <div className="fs-btn-row">
                  <button type="button" className={`fs-toggle${skipProblems ? ' on' : ''}`} onClick={() => setSkipProblems(true)}>Skip rows with problems</button>
                  <button type="button" className={`fs-toggle${!skipProblems ? ' on' : ''}`} onClick={() => setSkipProblems(false)}>Generate them anyway</button>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <p className="fs-muted fs-small">Exclude anything that doesn’t look right. Excluded creatives stay in the batch but are not exported or pushed.</p>
          )}

          {step === 6 && batch && (
            <div className="fs-col fs-gap-s">
              <div className="fs-eyebrow">Shared with</div>
              {(batch.shared_with || []).map((g) => (
                <div key={g.email} className="fs-map">
                  <span className="fs-small">{g.email}</span>
                  <button type="button" className="fs-link fs-bad" onClick={() => removeShare(g.email)} disabled={busy === 'share'} aria-label={`Remove ${g.email}`}>Remove</button>
                </div>
              ))}
              <div className="fs-btn-row">
                <input className="form-input fs-grow" type="email" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="name@company.com" aria-label="Email to share with"
                  onKeyDown={(e) => { if (e.key === 'Enter') addShare(); }} />
                <button type="button" className="btn btn-secondary" onClick={addShare} disabled={busy === 'share'}>Add</button>
              </div>
              <p className="fs-muted fs-small">They sign in with a Google account for that address to open a link. Removing someone stops further viewing; downloads can’t be recalled.</p>
              <button type="button" className="btn btn-secondary fs-mt" onClick={exportLinks}><Icon name="download" size={14} />Download links (.xlsx)</button>
              <label className="fs-field fs-mt">Campaign type
                <select className="form-input" value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
                  {CAMPAIGN_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <button type="button" className="btn btn-brass" onClick={push} disabled={busy === 'push' || !notPushed.length}>
                {busy === 'push' ? 'Pushing…' : notPushed.length ? `Push ${notPushed.length} to Creative Library` : 'All included creatives are in the library'}
              </button>
              {pending.length > 0 && (
                <button type="button" className="btn btn-approve" onClick={approveBatch} disabled={busy === 'approve'}>
                  {busy === 'approve' ? 'Approving…' : `Approve ${pending.length} and upload to Meta`}
                </button>
              )}
              <p className="fs-muted fs-small">Pushed as one batch, pending approval. Approval uploads each creative to Meta, as for any other upload.</p>
            </div>
          )}

          <hr />
          <div className="fs-btn-row">
            <button type="button" className="btn btn-secondary fs-grow" onClick={() => goTo(step - 1)} disabled={step === 1 || busy === 'generate'}>Back</button>
            {step < 6 && (
              <button type="button" className="btn btn-brass fs-grow2" onClick={goNext} disabled={busy === 'generate' || busy === 'ai'}>
                {busy === 'generate' ? 'Generating…' : nextLabel}
              </button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
